import { useMutation } from 'react-query';
import { shallow } from 'zustand/shallow';
import usePlanStore from '../../../../../../store/planStore';
import { getMetaConfig } from '../../../../../../api/services/mediaPlanning/touchpointConfigs';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';
import { Item } from '../../../common/DefaultSelect/DefaultSelect';

const useMetaConfig = () => {
  const {
    selectedCountry,
    selectedCountryISOCode,
    countryForms,
    generalInfoState,
    updateMetaConfig,
  } = usePlanStore();

  const { showError } = useError();

  const selectedCountryForm = countryForms[selectedCountry!];
  const metaConfig = selectedCountryForm?.metaConfig;

  const selectedCountryFormSubset = usePlanStore((state) => {
    const form = state.savedCountryForms[state.selectedCountry!];
    return form
      ? {
          targetGroup: form.targetGroup,
          campaignScenario: form.campaignScenario,
          segments: form.segments,
          startDate: form.startDate,
          campaignLength: form.campaignLength,
          isCountrySetupConfirmed: form.isCountrySetupConfirmed,
        }
      : null;
  }, shallow);

  const {
    mutate: fetchMetaConfig,
    isLoading,
    data,
    error,
  } = useMutation({
    mutationFn: (metaTargetGroup?: Item) => {
      if (
        !generalInfoState.division ||
        !selectedCountry ||
        !selectedCountryISOCode ||
        !countryForms[selectedCountry]?.campaignScenario
      ) {
        return Promise.reject(
          new Error('Missing required parameters for fetching meta config.')
        );
      }

      return getMetaConfig(
        generalInfoState.division,
        generalInfoState.brand,
        generalInfoState.category,
        selectedCountryISOCode,
        countryForms[selectedCountry].targetGroup,
        countryForms[selectedCountry].campaignScenario,
        (metaTargetGroup?.value as string) ?? metaConfig?.targetGroup,
        metaConfig?.startDate,
        metaConfig?.campaignLength,
        countryForms[selectedCountry].isTVIncluded
      );
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message || err.message || 'Unknown error';
      showError(errorMessage);
    },
    onSuccess: (response, metaTargetGroup) => {
      if (selectedCountry) {
        const rawTargetGroups: any[] = response?.data?.targetGroups ?? [];
        const targetGroups: string[] = rawTargetGroups.map((tg) =>
          typeof tg === 'object' && tg !== null ? tg.targetGroup : tg
        );
        const rawRecommended = response.data.recommendedTargetGroup;
        const recommendedTargetGroup =
          typeof rawRecommended === 'object' && rawRecommended !== null
            ? (rawRecommended as any).targetGroup
            : rawRecommended;

        const resolvedTargetGroup =
          (metaTargetGroup?.value as string) ?? recommendedTargetGroup ?? null;

        updateMetaConfig(selectedCountry, {
          saturation: response?.data?.saturationDefault || undefined,
          partnerBudgetShareConsideration: 0,
          campaignLength: countryForms[selectedCountry].campaignLength,
          partners: [],
          targetGroup: resolvedTargetGroup,
          targetGroups,
          startDate:
            response?.data?.startDate ??
            countryForms[selectedCountry].startDate,
          isGoldenRuleMet: response?.data?.isGoldenRuleMet,
          saturationMin: response?.data?.saturationMin ?? null,
          saturationMax: response?.data?.saturationMax ?? null,
          saturationDefault: response?.data?.saturationDefault ?? null,
          reachEstimateMin: response.data.reachEstimateMin ?? null,
          recommendedTargetGroup,
          metaMinReachFactor: response.data.metaMinReachFactor ?? null,
          isMaxSaturationExtended:
            response.data.isMaxSaturationExtended ?? false,
          metaAccountId: response.data.metaAccountId ?? '',
          minReach: response.data.minReach ?? null,
          grReach: response.data.grReach ?? null,
        });
      }
    },
  });

  return {
    fetchMetaConfig,
    isFetching: isLoading,
    data,
    error,
  };
};

export default useMetaConfig;
